import * as Mui from "@mui/material";
import { useFormik } from "formik";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import AutoComplete, { AutoCompleteOption } from "../../../common/AutoComplete";
import { nameof } from "../../../common/ObjectUtils";
import { EquipmentKitRequests, EquipmentKitService } from "../EquipmentKitService";
import { useEquipmentKitTabTranslation } from "../Utils";

interface Props {
  tenant:string;
  isOpen: boolean;
  onClose: () => void;
  equipmentKitId?: number;
  onUpdateDatasource?: () => void;
}

const EquipmentKitDialog = (props: Props) => {
  const { dispatchGlobalData } = useContext(GlobalContext);

  const refForm = useRef<HTMLFormElement>(null);

  const [equipments, setEquipments] = useState<AutoCompleteOption[]>([]);

  const { t } = useEquipmentKitTabTranslation();

  const onClose = () => {
    props.onClose();
    formik.resetForm();
  }

  const onSubmit = (data: EquipmentKitRequests.Create) => {
    if (!isEdit) {
      EquipmentKitService.create.equipmentKit(props.tenant,data).then(
        response => {
          props.onClose();
          formik.resetForm();
          
          dispatchGlobalData({
            type: setSnackbar,
            payload: {
              snackbar: {
                message: t("createEquipmentKit.succesfull"),
                type: snackbarTypes.success,
                show: true,
                autoClose: true,
              },
            },
          });

          props.onUpdateDatasource && props.onUpdateDatasource();
        },
        () => {
          props.onClose();
          formik.resetForm();
          
          dispatchGlobalData({
            type: setSnackbar,
            payload: {
              snackbar: {
                message: t("createEquipmentKit.errorCreate"),
                type: snackbarTypes.error,
                show: true,
                autoClose: true,
              },
            },
          });
        }
      )
    } else if (isEdit && props.equipmentKitId) {
      EquipmentKitService.update.kit(props.tenant,props.equipmentKitId, data).then(
        () => {
          props.onClose();
          formik.resetForm();
          
          dispatchGlobalData({
            type: setSnackbar,
            payload: {
              snackbar: {
                message: t("createEquipmentKit.updated"),
                type: snackbarTypes.success,
                show: true,
                autoClose: true,
              },
            },
          });

          props.onUpdateDatasource && props.onUpdateDatasource();
        },
        () => {
          props.onClose();
          formik.resetForm();
          
          dispatchGlobalData({
            type: setSnackbar,
            payload: {
              snackbar: {
                message: t("createEquipmentKit.errorUpdate"),
                type: snackbarTypes.error,
                show: true,
                autoClose: true,
              },
            },
          });
        }
      )
    }
  }

  const formik = useFormik<EquipmentKitRequests.Create>({ initialValues: {
    code: "",
    descr: "",
    equipmentsId: [],
  }, onSubmit: onSubmit });

  useEffect(() => {
    if (props.isOpen) {
      EquipmentKitService.get.valids(props.tenant).then(
        response => {
          setEquipments(response.map(res => ({
            label: res.descr,
            value: res.id,
          })));
        }
      ).then(
        () => {
          if (props.equipmentKitId) {
            EquipmentKitService.get.byId(props.tenant,props.equipmentKitId).then(
              response => {
                formik.setValues({
                  code: response.code,
                  descr: response.descr,
                  equipmentsId: response.equipments.map(eq => eq.id),
                });
              }
            )
          }
        }
      )
    }
  }, [props.isOpen]);

  const isEdit = useMemo(() => props.equipmentKitId ? true : false, [props.equipmentKitId]);
  return (
    <Mui.Dialog open={props.isOpen} onClose={onClose} fullWidth={true} maxWidth="md">
      <Mui.DialogTitle>
        {!isEdit ? (
          <Mui.Typography variant="h5">{t("createEquipmentKit.createTitle")}</Mui.Typography>
        ) : (
          <Mui.Typography variant="h5">{t("createEquipmentKit.editTitle")}</Mui.Typography>
        )}
      </Mui.DialogTitle>

      <Mui.DialogContent>
        <form ref={refForm} onSubmit={formik.handleSubmit}>
          <Mui.Grid container spacing="16px">
            <Mui.Grid item md={4} xs={12}>
              <Mui.TextField
                name={nameof<EquipmentKitRequests.Create>("code")}
                label={t("createEquipmentKit.fields.equipmentKitCode")}
                fullWidth={true}
                onChange={formik.handleChange}
                value={formik.values.code}
              />
            </Mui.Grid>

            <Mui.Grid item md={4} xs={12}>
              <Mui.TextField
                name={nameof<EquipmentKitRequests.Create>("descr")}
                label={t("createEquipmentKit.fields.description")}
                fullWidth={true}
                onChange={formik.handleChange}
                value={formik.values.descr}
              />
            </Mui.Grid>

            <Mui.Grid item md={4} xs={12}>
              <AutoComplete
                multiple={true}
                options={equipments}
                label={t("createEquipmentKit.fields.equipment")}
                value={formik.values.equipmentsId}
                onChange={(value) => formik.setFieldValue(nameof<EquipmentKitRequests.Create>("equipmentsId"), value)}
                fullWidth={true}
              />
            </Mui.Grid>
          </Mui.Grid>
        </form>
      </Mui.DialogContent>
          
      <Mui.DialogActions>
        <Mui.Button onClick={onClose} variant="outlined">
          {t("createEquipmentKit.actions.cancel")}
        </Mui.Button>
        <Mui.Button type="submit" onClick={() => formik.submitForm()}>
          {t("createEquipmentKit.actions.save")}
        </Mui.Button>
      </Mui.DialogActions>
    </Mui.Dialog>
  )
}

export default EquipmentKitDialog;